import React, { useState } from 'react';
import { Page } from '../../layout';
import PageInstructions from '../../shared/components/PageInstructions';
import ResponsiveTabMenu from '../../shared/components/ResponsiveTabMenu';
import useUriFragment from './../../shared/hooks/useUriFragment';
import { useTabExistCheck } from '../../shared/hooks/useTabExistCheck';
import { Breadcrumb } from '../../shared/components/breadcrumb/Breadcrumb';
import { getActiveTabDisplayName } from '../../shared/services/getActiveTabDisplayNameService';
import { useMutation, useQuery, useReactiveVar } from '@apollo/client';
import GET_RESOURCES from '../queries/getResourcesQuery';
import ApolloError from '@shared/components/ApolloError';
import ResourcesMapper from '../mappers/ResourcesMapper';
import ActionButtonsGroup from '../../shared/components/ActionButtonsGroup';
import PrivilegeNames from '../../../constants/privilegeNames';
import { userPrivileges } from '@apollo-internal/cache';
import RESOURCE_TABS from '../constants/resourceTabs';
import UPDATE_RESOURCES from '../queries/updateResourcesMutation';
import ResourcesSection from './ResourcesSection';
import EditResourcesSection from './EditResourcesSection';
import { useIsSubmitDisabledStore } from '../../shared/stores/FormSubmitBtnDisabledStore';
import { EDIT_RESOURCE_FORM } from '@shared/constants/editResourceFormKey';
import { isEqual } from 'lodash';
import { useShallow } from 'zustand/shallow';

const ResourcePage = () => {
  const privileges = useReactiveVar(userPrivileges);
  const isUserEligibleToEdit = privileges.includes(PrivilegeNames.Resources_Edit);

  const isSubmitDisabled = useIsSubmitDisabledStore(
    useShallow(state => state.isSubmitDisabled(EDIT_RESOURCE_FORM))
  );
  const setIsSubmitDisabled = useIsSubmitDisabledStore.getState().setIsSubmitDisabled;

  const tabs = RESOURCE_TABS;
  const [activeTab, setActiveTab] = useUriFragment(tabs);
  const activeTabDisplayName = getActiveTabDisplayName(tabs, activeTab);
  const pageTitle = activeTabDisplayName;
  const breadcrumbPathItems = [
    { name: 'Resources', url: '/resources' },
    { name: activeTabDisplayName, url: '' }
  ];

  const [resources, setResources] = useState([]);
  const [isEdit, setIsEdit] = useState(false);

  useTabExistCheck(tabs, activeTab);

  const {
    data: resourcesData,
    error: resourcesDataError,
    loading: resourcesDataLoading
  } = useQuery(GET_RESOURCES, {
    onCompleted: resourcesData => {
      if (resourcesData?.resources) {
        setResources(ResourcesMapper.toResourcesData(resourcesData.resources));
      }
    }
  });

  const [updateResources, { error: updateResourcesError }] = useMutation(UPDATE_RESOURCES, {
    update: (cache, { data: { updateResources: resources } }) => {
      cache.writeQuery({ query: GET_RESOURCES, data: { resources } });
      setResources(ResourcesMapper.toResourcesData(resources));
    }
  });

  const handleFormSubmit = async submittedResourceFormData => {
    const resourceFormData = submittedResourceFormData.resources.map(
      ResourcesMapper.mapResourcePermissions
    );

    const updatedResources = getAddedAndEditedResources(resources, resourceFormData);
    const removedResourceIds = getRemovedResourceIds(resources, resourceFormData);

    await updateResources({
      variables: {
        updatedResources: updatedResources.map(ResourcesMapper.toResourcesInput),
        removedResourceIds: removedResourceIds
      }
    });

    setIsSubmitDisabled(EDIT_RESOURCE_FORM, true);
    setIsEdit(false);
  };

  const handleCancelClick = async () => {
    setResources(ResourcesMapper.toResourcesData(resourcesData?.resources));
    setIsSubmitDisabled(EDIT_RESOURCE_FORM, true);
    setIsEdit(false);
  };

  const handleEditClick = () => {
    setIsEdit(true);
  };

  if (resourcesDataError || updateResourcesError) {
    return <ApolloError />;
  }

  return (
    <Page title={pageTitle} className='resources'>
      <Breadcrumb pathItems={breadcrumbPathItems} />
      <PageInstructions
        title='Resource Page Instructions'
        description={isUserEligibleToEdit ? editPageDescription : pageDescription}
      />
      <div className='resources-tab'>
        <ResponsiveTabMenu
          activeTab={activeTab}
          tabs={tabs}
          onChange={setActiveTab}
          loading={resourcesDataLoading}
        />
        {isUserEligibleToEdit && (
          <ActionButtonsGroup
            isEdit={isEdit}
            handleEditClick={handleEditClick}
            handleCancelClick={handleCancelClick}
            handleSubmitClick={() => {}}
            submitForm={EDIT_RESOURCE_FORM}
            submitButtonType='submit'
            isSubmitting={resourcesDataLoading}
            isSubmitDisabled={isSubmitDisabled}
          />
        )}
      </div>
      {isEdit ? (
        <EditResourcesSection
          resources={resources}
          handleFormSubmit={handleFormSubmit}
          activeTab={activeTab}
        />
      ) : (
        !resourcesDataLoading && <ResourcesSection resources={resources} activeTab={activeTab} />
      )}
    </Page>
  );
};

const getAddedAndEditedResources = (existingResources, updatedResources) =>
  updatedResources.filter(updatedResource => {
    const existingResource = existingResources.find(
      x => x.resourceId === updatedResource.resourceId
    );

    if (!existingResource) {
      return true;
    }

    return !isEqual(existingResource, updatedResource);
  });

const getRemovedResourceIds = (existingResources, updatedResources) =>
  existingResources
    .filter(x => !updatedResources.some(e => e.resourceId === x.resourceId))
    .map(x => x.resourceId);

const pageDescription =
  'All available VIP Resources are listed below. ' +
  'Search by keyword or select a category tab to refine results.';

const editPageDescription =
  'All available VIP Resources are listed below. ' +
  'Search by keyword or select a category tab to refine results. ' +
  'Admins may use the Edit button to update existing resources, ' +
  'remove resources using the x in the upper right corner, ' +
  'or add new resources using the form at the bottom of the page.';

export default ResourcePage;
